import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Tag, Select, Input, Row, Col, Form, Tooltip, Space, Badge, Tabs, Alert, Upload, Modal,
} from 'antd';
import CBInputNumber from '../../components/CBInputNumber';
import {
  PlusOutlined, PrinterOutlined, DeleteOutlined, SendOutlined,
  CheckCircleOutlined, FileAddOutlined, ReloadOutlined,
  BellOutlined, ExclamationCircleOutlined, FileDoneOutlined,
  PaperClipOutlined, CopyOutlined, CloseOutlined,
  EditOutlined, MailOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { Trans, useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import { chargerPartenaires, optionsPartenaires } from '../../services/echangesPartenaires';
import SinistreSearch from '../../components/SinistreSearch';
import { toast } from 'react-toastify';
import CBModal from '../../components/CBModal';
import TableExportButton from '../../components/TableExportButton';
import PageHeader from '../../components/PageHeader';
import SelectListeParametrable from '../../components/SelectListeParametrable';
import logoCarteBrune from '../../assets/images/logo.png';
import i18n from '../../i18n';
import { avecLibelleTraduit, libellesTraduits } from '../../i18n/libelles';
import { ancienneteBordereau, SEUIL_RELANCE_JOURS } from './ancienneteBordereau';
import { largeurTotale } from './affichageCourriers';
import { actionsPossibles } from './actionsBordereau';
const GREEN = '#1a6b3c';

const STATUT_COLOR = {
  BROUILLON: 'default',
  IMPRIME: 'blue',
  REMIS_TRANSPORTEUR: 'orange',
  DECHARGE_RECUE: 'green',
  RETOURNE: 'red',
};

const STATUT_LABEL = libellesTraduits('courriers:bordereaux.statuts');

const TRANSPORTEURS = avecLibelleTraduit([
  { value: 'COURSIER_INTERNE' },
  { value: 'POSTE' },
  { value: 'BUS' },
  { value: 'DHL' },
  { value: 'AUTRE' },
], 'courriers:bordereaux.transporteurs');

/* Libellés en français, à dessein : ils partent sur le bordereau imprimé (remis
   au coursier et au destinataire) et dans l'objet enregistré des courriers. La
   liste de saisie, elle, les affiche dans la langue de l'interface. */

const NATURES = [
  { value: 'BAP', label: 'BAP' },
  { value: 'CONFIRMATION_GARANTIE', label: 'Confirmation de garantie' },
  { value: 'TRANSMISSION_EXPERTISE', label: "Transmission d'expertise" },
  { value: 'OFFRE_INDEMNISATION', label: "Offre d'indemnisation" },
  { value: 'RELANCE', label: 'Relance' },
  { value: 'DEMANDE_PIECES', label: 'Demande de pièces' },
  { value: 'DEMANDE_OFFRE', label: "Demande d'offre" },
  { value: 'ACCORD_VICTIME', label: 'Accord victime' },
  { value: 'TRANSMISSION_PV', label: 'Transmission PV' },
  { value: 'COMPTE_RENDU_AUDIENCE', label: "Compte rendu d'audience" },
  { value: 'AUTRE', label: 'Autre' },
];

//  Ligne vide 
const newLigne = () => ({
  _id: Math.random().toString(36).slice(2),
  sinistreTrackingId: null,
  numeroSinistreLocal: '',
  nature: null,
  objet: '',
  referenceCourrier: '',
  typeCourrier: 'SORTANT',
  /** Scan du courrier signé (File) - classé dans la GED du dossier à l'enregistrement. */
  piece: null,
});

/**
 * Une ligne compte dès qu'elle porte quelque chose. Les lignes ajoutées par
 * réflexe et jamais remplies sont ignorées à l'enregistrement : sans ce filtre,
 * chacune créait un courrier « - » au registre, avec une référence consommée.
 */
const ligneRenseignee = (l) =>
  !!(l.sinistreTrackingId || l.objet?.trim() || l.referenceCourrier?.trim() || l.piece);

/* LE SEUIL VIT AVEC LA REGLE QUI S'EN SERT - voir `ancienneteBordereau`. Declare ici
   ET la-bas, il aurait fini par differer : la colonne aurait signale un retard que
   l'onglet « À relancer » ne compterait pas. */
const joursDepuis = (d) => d ? dayjs().diff(dayjs(d), 'day') : null;

//  Composant principal 
export default function Bordereaux() {
  const { t } = useTranslation('courriers');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bureaux, setBureaux] = useState([]);
  const [filtreStatut, setFiltreStatut] = useState(null);
  const [activeTab, setActiveTab] = useState('tous');

  // Modal relance
  const [relanceModal, setRelanceModal] = useState(null);
  const [relanceForm] = Form.useForm();
  const [savingRelance, setSavingRelance] = useState(false);

  // Saisie d'un nouveau bordereau (tableau en pleine page)
  const [editionOuverte, setEditionOuverte] = useState(false);
  /* Le bordereau en cours de MODIFICATION, ou null en creation. Il porte le detail
     frais du serveur, pas la ligne du tableau : le tableau n'a pas les lignes de
     courriers. */
  const [bordereauEdite, setBordereauEdite] = useState(null);
  /* Le bordereau dont on gere les courriers embarques, et les candidats a embarquer. */
  const [courriersModal, setCourriersModal] = useState(null);
  const [candidats, setCandidats] = useState([]);
  const [occupe, setOccupe] = useState(false);
  const [lignes, setLignes] = useState([newLigne()]);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  // Modal remis-transporteur
  const [transporteurModal, setTransporteurModal] = useState(null);
  const [transporteurForm] = Form.useForm();

  // Modal décharge
  const [dechargeModal, setDechargeModal] = useState(null);
  const [dechargeForm] = Form.useForm();

  // Détail / impression

  const [aRelancerList, setARelancerList] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, resAR] = await Promise.all([
        axiosInstance.get('/v1/bordereaux'),
        axiosInstance.get(`/v1/bordereaux/a-relancer?seuilJours=${SEUIL_RELANCE_JOURS}`),
      ]);
      setList(res.data?.data || []);
      setARelancerList(resAR.data?.data || []);
    } catch { toast.error(i18n.t('courriers:bordereaux.erreurChargement')); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    // Compagnies ET bureaux homologues : un bordereau part aussi à une compagnie,
    // qui sans cela n'était saisissable qu'en texte libre - hors de ses états.
    chargerPartenaires().then(({ bureaux: b, compagnies }) => setBureaux([...b, ...compagnies]));
  }, [load]);

  // Source : serveur pour l'onglet "À relancer" (filtre + tri par ancienneté côté backend)
  const baseList = activeTab === 'a-relancer' ? aRelancerList : list;
  const filtered = (activeTab === 'a-relancer' || !filtreStatut)
    ? baseList
    : baseList.filter(b => b.statut === filtreStatut);

  //  Gestion lignes 
  const addLigne = () => setLignes(prev => [...prev, newLigne()]);

  // La pièce n'est pas dupliquée : deux courriers ne partagent pas le même scan.
  const dupLigne = (idx) => {
    const src = lignes[idx];
    setLignes(prev => {
      const next = [...prev];
      next.splice(idx + 1, 0, { ...src, _id: Math.random().toString(36).slice(2), piece: null });
      return next;
    });
  };

  const ouvrirEdition = () => {
    setBordereauEdite(null);
    setLignes([newLigne()]);
    form.resetFields();
    setEditionOuverte(true);
  };

  /**
   * Ouvre le panneau sur un bordereau BROUILLON existant.
   *
   * ON RELIT LE DETAIL AU SERVEUR. La ligne du tableau ne porte pas les courriers
   * embarques, et elle peut avoir vieilli - un autre agent a pu imprimer ce bordereau
   * entre-temps, ce qui le rend non modifiable.
   *
   * LES COURRIERS NE SONT PAS CHARGES DANS LE FORMULAIRE, et ce n'est pas un oubli :
   * `PUT /v1/bordereaux/{id}` ne touche PAS aux lignes. Les y afficher modifiables
   * ferait saisir des changements que le serveur ignorerait en silence - le pire des
   * resultats. Ils se gerent par « Courriers », un par un.
   */
  const ouvrirModification = async (b) => {
    try {
      const res = await axiosInstance.get(`/v1/bordereaux/${b.bordereauTrackingId}`);
      const detail = res.data?.data;
      if (!detail) return;
      if (detail.statut !== 'BROUILLON') {
        toast.warning(t('bordereaux.saisie.nonModifiable', { statut: STATUT_LABEL[detail.statut] || detail.statut }));
        load();
        return;
      }
      form.setFieldsValue({
        destinataireOrganismeTrackingId: detail.destinataireOrganismeTrackingId || undefined,
        destinataireLibre: detail.destinataireLibre || undefined,
        lieuDepart: detail.lieuDepart || 'Lomé',
        transporteur: detail.transporteur || undefined,
      });
      setBordereauEdite(detail);
      setLignes([]);
      setEditionOuverte(true);
    } catch (_) { /* message du serveur affiche par l'intercepteur */ }
  };

  const fermerEdition = () => {
    setEditionOuverte(false);
    setBordereauEdite(null);
    setLignes([newLigne()]);
    form.resetFields();
  };
  const annulerEdition = () => {
    // En modification, les lignes ne sont pas saisies : il n'y a rien a perdre.
    if (bordereauEdite || !lignes.some(ligneRenseignee)) { fermerEdition(); return; }
    Modal.confirm({
      title: t('bordereaux.abandon.titre'),
      content: t('bordereaux.abandon.texte'),
      okText: t('bordereaux.abandon.ok'), cancelText: t('bordereaux.abandon.annuler'), okButtonProps: { danger: true },
      onOk: fermerEdition,
    });
  };

  const removeLigne = (id) => setLignes(prev => prev.filter(l => l._id !== id));

  const updateLigne = (id, patch) =>
    setLignes(prev => prev.map(l => l._id === id ? { ...l, ...patch } : l));

  // L'objet déjà saisi est conservé : on tape souvent l'objet avant de chercher
  // le dossier, et l'ancien écran l'effaçait à ce moment-là.
  const onSinistreSelect = (id, sinistre, ligneId) => {
    setLignes(prev => prev.map(l => l._id === ligneId ? {
      ...l,
      sinistreTrackingId: sinistre.sinistreTrackingId,
      numeroSinistreLocal: sinistre.numeroSinistreLocal || '',
      objet: l.objet || sinistre.objet || '',
    } : l));
  };

  //  Enregistrer le bordereau 
  const enregistrer = async () => {
    let vals;
    try { vals = await form.validateFields(); } catch { return; }

    /* EN MODIFICATION, SEUL L'EN-TETE PART. `PUT /v1/bordereaux/{id}` ne touche pas
       aux lignes de courriers - elles s'ajoutent et se retirent une par une. Reprendre
       ici le parcours de creation creerait de nouveaux courriers a chaque
       enregistrement. */
    if (bordereauEdite) {
      setSaving(true);
      try {
        await axiosInstance.put(`/v1/bordereaux/${bordereauEdite.bordereauTrackingId}`, {
          destinataireOrganismeTrackingId: vals.destinataireOrganismeTrackingId || null,
          destinataireLibre: vals.destinataireLibre || null,
          lieuDepart: vals.lieuDepart || 'Lomé',
          transporteur: vals.transporteur,
        });
        toast.success(t('bordereaux.saisie.modifie'));
        fermerEdition();
        load();
      } catch (_) { /* message du serveur affiche par l'intercepteur */ }
      finally { setSaving(false); }
      return;
    }

    const aEnvoyer = lignes.filter(ligneRenseignee);
    if (aEnvoyer.length === 0) { toast.warn(t('bordereaux.saisie.aucuneLigne')); return; }

    // La GED est construite par dossier : un scan sans sinistre n'a nulle part où aller.
    const scanSansDossier = aEnvoyer.find(l => l.piece && !l.sinistreTrackingId);
    if (scanSansDossier) {
      toast.warn(t('bordereaux.saisie.scanSansDossier', { n: lignes.indexOf(scanSansDossier) + 1 }));
      return;
    }

    // Le référentiel des organismes expose `raisonSociale` (c'est ce que la liste
    // affiche) : `nom` n'existait pas, et tout bordereau partait « Destinataire ».
    const bureauSel = vals.destinataireOrganismeTrackingId
      ? bureaux.find(b => b.organismeTrackingId === vals.destinataireOrganismeTrackingId)
      : null;
    const destinataireStr = vals.destinataireLibre || bureauSel?.raisonSociale || 'Destinataire';

    setSaving(true);
    try {
      // Étape 1 : un courrier SORTANT par ligne. Quand la ligne porte un scan, il
      // part en multipart et le serveur le classe dans la GED du dossier - même
      // point d'entrée que « Enregistrer un courrier », rien de nouveau côté API.
      const courrierIds = [];
      let piecesNonClassees = 0;
      for (const l of aEnvoyer) {
        const metadata = {
          sinistreTrackingId: l.sinistreTrackingId || null,
          typeCourrier: 'SORTANT',
          nature: l.nature || 'AUTRE',
          objet: l.objet?.trim() || (NATURES.find(n => n.value === l.nature)?.label) || '-',
          referenceCourrier: l.referenceCourrier?.trim() || null,
          expediteur: 'BNCB-TG',
          destinataire: destinataireStr,
          destinataireOrganismeTrackingId: vals.destinataireOrganismeTrackingId || null,
          dateCourrier: dayjs().format('YYYY-MM-DD'),
          canal: 'COURRIER_POSTAL',
        };
        let res;
        if (l.piece) {
          const fd = new FormData();
          fd.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
          fd.append('file', l.piece);
          res = await axiosInstance.post('/v1/courriers', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          // Le courrier est enregistré même si la GED refuse ou est indisponible :
          // le serveur le dit dans son message, on le compte pour prévenir l'agent.
          if (/non classée|indisponible/i.test(res.data?.message || '')) piecesNonClassees += 1;
        } else {
          res = await axiosInstance.post('/v1/courriers', metadata);
        }
        const id = res.data?.data?.courrierTrackingId;
        if (id) courrierIds.push(id);
      }

      if (courrierIds.length === 0) { toast.error(t('bordereaux.saisie.aucunCourrierCree')); return; }

      // Étape 2 : le bordereau, avec les courriers dans l'ordre des lignes.
      const resB = await axiosInstance.post('/v1/bordereaux', {
        destinataireOrganismeTrackingId: vals.destinataireOrganismeTrackingId || null,
        destinataireLibre: vals.destinataireLibre || null,
        lieuDepart: vals.lieuDepart || 'Lomé',
        transporteur: vals.transporteur,
        courriersTrackingIds: courrierIds,
      });

      const numero = resB.data?.data?.numeroBordereau;
      toast.success(t('bordereaux.saisie.cree', { numero: numero || '', count: courrierIds.length }));
      if (piecesNonClassees > 0) {
        toast.warning(t('bordereaux.saisie.scansNonClasses', { count: piecesNonClassees }));
      }
      fermerEdition();
      load();
    } catch (_) {
      // L'intercepteur axios affiche déjà le message du serveur.
    } finally { setSaving(false); }
  };

  //  Courriers embarques d'un brouillon 
  /**
   * Ouvre la gestion des courriers d'un brouillon.
   *
   * Les candidats sont filtres sur le DESTINATAIRE du bordereau : embarquer un
   * courrier adresse a quelqu'un d'autre est l'erreur que ce filtre previent, et le
   * serveur l'accepterait sans broncher.
   */
  const ouvrirCourriers = async (b) => {
    try {
      const [detail, prets] = await Promise.all([
        axiosInstance.get(`/v1/bordereaux/${b.bordereauTrackingId}`),
        axiosInstance.get('/v1/bordereaux/courriers-prets-a-embarquer', {
          params: b.destinataireOrganismeTrackingId
            ? { destinataire: b.destinataireOrganismeTrackingId }
            : undefined,
        }),
      ]);
      setCourriersModal(detail.data?.data || null);
      setCandidats(prets.data?.data || []);
    } catch (_) { /* message du serveur affiche par l'intercepteur */ }
  };

  /** Recharge le panneau ET le tableau : le nombre de courriers y est affiche. */
  const rafraichirCourriers = async (bordereauId) => {
    const [detail, prets] = await Promise.all([
      axiosInstance.get(`/v1/bordereaux/${bordereauId}`),
      axiosInstance.get('/v1/bordereaux/courriers-prets-a-embarquer', {
        params: courriersModal?.destinataireOrganismeTrackingId
          ? { destinataire: courriersModal.destinataireOrganismeTrackingId }
          : undefined,
      }),
    ]);
    setCourriersModal(detail.data?.data || null);
    setCandidats(prets.data?.data || []);
    load();
  };

  const ajouterCourrier = async (courrierId) => {
    setOccupe(true);
    try {
      await axiosInstance.post(`/v1/bordereaux/${courriersModal.bordereauTrackingId}/courriers/${courrierId}`);
      await rafraichirCourriers(courriersModal.bordereauTrackingId);
    } catch (_) { /* message du serveur */ }
    finally { setOccupe(false); }
  };

  const retirerCourrier = async (courrierId) => {
    setOccupe(true);
    try {
      await axiosInstance.delete(`/v1/bordereaux/${courriersModal.bordereauTrackingId}/courriers/${courrierId}`);
      await rafraichirCourriers(courriersModal.bordereauTrackingId);
    } catch (_) { /* message du serveur */ }
    finally { setOccupe(false); }
  };

  //  Supprimer un brouillon 
  const supprimer = (b) => Modal.confirm({
    title: t('bordereaux.suppression.titre'),
    content: t('bordereaux.suppression.texte', { numero: b.numeroBordereau || '' }),
    okText: t('bordereaux.suppression.ok'), cancelText: t('bordereaux.suppression.annuler'), okButtonProps: { danger: true },
    onOk: async () => {
      try {
        await axiosInstance.delete(`/v1/bordereaux/${b.bordereauTrackingId}`);
        toast.success(t('bordereaux.suppression.succes'));
        load();
      } catch (_) { /* message du serveur */ }
    },
  });

  //  Imprimer 
  const imprimer = async (b) => {
    try {
      let bordereau;
      if (b.statut === 'BROUILLON') {
        const patchRes = await axiosInstance.patch(`/v1/bordereaux/${b.bordereauTrackingId}/imprimer`);
        bordereau = patchRes.data?.data || b;
        load();
      } else {
        const detRes = await axiosInstance.get(`/v1/bordereaux/${b.bordereauTrackingId}`);
        bordereau = detRes.data?.data || b;
      }
      ouvrirImpression(bordereau);
    } catch { }
  };

  const ouvrirImpression = (bordereau) => {
    const today = dayjs().format('DD/MM/YYYY');
    const courriers = bordereau.lignes || bordereau.courriers || [];
    const minRows = 8;
    const emptyCount = Math.max(0, minRows - courriers.length);
    const escape = (s) => String(s ?? '').replace(/[&<>"']/g, c => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));

    const rows = courriers.map((c, i) => {
      const numSin = c.numeroSinistreOurRef ?? c.sinistreNumeroLocal ?? '';
      const natureV = c.objet ?? c.nature ?? '';
      const objet = c.objetDetaille ?? '';
      const ref = c.referenceCourrier ?? '';
      const natureLabel = NATURES.find(n => n.value === natureV)?.label || natureV;
      return `<tr>
        <td class="c">${i + 1}</td>
        <td class="mono"><strong>${escape(numSin) || '-'}</strong></td>
        <td>${escape(objet)}</td>
        <td>${escape(natureLabel)}</td>
        <td class="mono">${escape(ref)}</td>
        <td></td>
      </tr>`;
    }).join('');

    const empties = Array.from({ length: emptyCount }).map((_, i) => `
      <tr class="empty"><td class="c">${courriers.length + i + 1}</td><td></td><td></td><td></td><td></td><td></td></tr>
    `).join('');

    /* URL ABSOLUE, et non le chemin relatif que fournit l'empaqueteur : le
       document est écrit dans une fenêtre `about:blank`, où un chemin relatif
       n'a aucune base contre laquelle se résoudre. Le logo s'affichait à
       l'écran de développement et manquait à l'impression. */
    const urlLogo = new URL(logoCarteBrune, window.location.origin).href;

    const html = `<!doctype html>
<html lang="fr"><head><meta charset="utf-8"/>
<title>Bordereau ${escape(bordereau.numeroBordereau)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 11.5px; color: #000; margin: 0; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 10px; }
  .header .left { width: 35%; display: flex; gap: 8px; align-items: flex-start; }
  .header .logo { width: 46px; height: auto; flex-shrink: 0; }
  .header .center { flex: 1; text-align: center; }
  .header .right { width: 25%; text-align: right; font-size: 11px; }
  .org-name { font-weight: 700; font-size: 13px; color: #1a6b3c; }
  .org-sub { font-size: 10.5px; color: #444; }
  .title { font-weight: 700; font-size: 14px; text-transform: uppercase; letter-spacing: 0.5px; }
  .num { margin-top: 4px; font-size: 11px; }
  hr { border: 0; border-top: 1px solid #000; margin: 8px 0 12px; }
  .parties { display: flex; gap: 16px; margin-bottom: 8px; }
  .parties > div { flex: 1; }
  .label-mini { font-size: 10px; text-transform: uppercase; color: #555; margin-bottom: 2px; }
  table.bord { width: 100%; border-collapse: collapse; margin-top: 8px; }
  table.bord th, table.bord td { border: 1px solid #333; padding: 5px 7px; vertical-align: top; font-size: 11px; }
  table.bord th { background: #ececec; font-weight: 700; text-align: left; }
  table.bord tr.empty td { color: #bbb; height: 22px; }
  .c { text-align: center; }
  .mono { font-family: 'Courier New', monospace; }
  .total { margin-top: 8px; font-size: 10.5px; color: #444; }
  .signatures { display: flex; gap: 16px; margin-top: 36px; }
  .signatures .sig { flex: 1; text-align: center; }
  .sig-label { font-size: 10.5px; margin-bottom: 48px; }
  .sig-line { border-top: 1px solid #333; padding-top: 3px; font-size: 10.5px; }
  .footer { margin-top: 18px; font-size: 9px; color: #777; text-align: center; }
  /* MARGE DE PAGE À ZÉRO, MARGE RÉELLE DANS LE BODY.
     Ce n'est pas un choix de mise en page : c'est le seul moyen, depuis le
     document, de faire taire l'en-tête et le pied que le navigateur ajoute de
     lui-même - le titre de la page à gauche, son adresse à droite. Tant que
     @page garde une marge, le navigateur s'y installe, et le bordereau sortait
     surmonté de « Bordereau ... » et de l'URL de l'application.

     La marge d'impression n'est donc pas perdue, elle change de porteur : le
     body la reprend en centimètres. 1,2 cm en haut et en bas, 1,4 cm sur les
     côtés - largement au-delà de la zone non imprimable des imprimantes
     courantes, et de quoi perforer sans entamer le texte, ce qu'un bordereau
     classé au bureau réclame.

     LE LECTEUR PEUT LES REMETTRE. La case « En-têtes et pieds de page » de la
     boîte de dialogue d'impression reste la sienne ; cette règle commande le
     défaut, pas son choix. */
  @page { size: A4 landscape; margin: 0; }
  body { padding: 1.2cm 1.4cm; }
</style></head>
<body>
  <div class="header">
    <div class="left">
      <img class="logo" src="${urlLogo}" alt=""/>
      <div>
        <div class="org-name">BNCB - TOGO</div>
        <div class="org-sub">Bureau National Togolais</div>
        <div class="org-sub">de la Carte Brune CEDEAO</div>
      </div>
    </div>
    <div class="center">
      <div class="title">Bordereau de transmission de courrier</div>
      <div class="num">N° <strong style="font-family:'Courier New',monospace;font-size:12px">${escape(bordereau.numeroBordereau)}</strong></div>
    </div>
    <div class="right">
      <div>Date : <strong>${today}</strong></div>
      <div>Lieu : ${escape(bordereau.lieuDepart || 'Lomé')}</div>
    </div>
  </div>
  <hr/>
  <div class="parties">
    <div>
      <div class="label-mini">Expéditeur</div>
      <div style="font-weight:600">BNCB - Togo</div>
    </div>
    <div>
      <div class="label-mini">Destinataire</div>
      <div style="font-weight:600">${escape(bordereau.destinataireOrganismeLibelle || bordereau.destinataireLibre || '-')}</div>
    </div>
  </div>

  <table class="bord">
    <colgroup>
      <col style="width:5%"/><col style="width:20%"/><col style="width:32%"/>
      <col style="width:18%"/><col style="width:15%"/><col style="width:10%"/>
    </colgroup>
    <thead>
      <tr>
        <th class="c">N°</th><th>N° Sinistre</th><th>Objet</th>
        <th>Nature</th><th>Référence</th><th class="c">Obs.</th>
      </tr>
    </thead>
    <tbody>${rows}${empties}</tbody>
  </table>

  <div class="total">Total transmis : <strong>${courriers.length}</strong> courrier${courriers.length > 1 ? 's' : ''}</div>

  <div class="signatures">
    <div class="sig"><div class="sig-label">Pour le BNCB Togo</div><div class="sig-line">Signature expéditeur</div></div>
    <div class="sig"><div class="sig-label">Coursier / transporteur</div><div class="sig-line">Signature &amp; date</div></div>
    <div class="sig"><div class="sig-label">Réception destinataire</div><div class="sig-line">Cachet &amp; signature</div></div>
  </div>

  <div class="footer">Document généré automatiquement par CBConnect - ${today}</div>

  <script>
    window.addEventListener('load', function () {
      setTimeout(function () { window.print(); }, 150);
      window.addEventListener('afterprint', function () { window.close(); });
    });
  </script>
</body></html>`;

    const w = window.open('', '_blank', 'width=1100,height=800');
    if (!w) {
      toast.error(t('bordereaux.popupBloquee'));
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  //  Remis transporteur 
  const confirmerRemis = async (vals) => {
    try {
      await axiosInstance.patch(`/v1/bordereaux/${transporteurModal.bordereauTrackingId}/remis-transporteur`, {
        transporteur: vals.transporteur,
        nomCompagnieBus: vals.nomCompagnieBus || null,
        referenceTransporteur: vals.referenceTransporteur || null,
        montantTransporteur: vals.montantTransporteur || null,
      });
      toast.success(t('bordereaux.remise.confirmee'));
      setTransporteurModal(null);
      transporteurForm.resetFields();
      load();
    } catch (_) { /* message du serveur affiche par l'intercepteur */ }
  };

  //  Relance d'un bordereau sans décharge 
  const ouvrirRelance = (bordereau) => {
    setRelanceModal(bordereau);
    relanceForm.resetFields();
    relanceForm.setFieldsValue({
      canal: 'MAIL',
      objet: `Relance - Bordereau ${bordereau.numeroBordereau}`,
      observations:
        `Bordereau ${bordereau.numeroBordereau} transmis le ${dayjs(bordereau.dateRemiseTransporteur).format('DD/MM/YYYY')} via ${bordereau.transporteur || 'transport'}.
Sans décharge à ce jour (${joursDepuis(bordereau.dateRemiseTransporteur)} jours).
Merci de bien vouloir nous retourner la décharge signée.`,
    });
  };

  const envoyerRelance = async (vals) => {
    setSavingRelance(true);
    try {
      const b = relanceModal;
      // Récupérer les courriers du bordereau pour rattacher la relance au 1er sinistre
      let sinistreId = null;
      try {
        const det = await axiosInstance.get(`/v1/bordereaux/${b.bordereauTrackingId}`);
        const lignes = det.data?.data?.lignes || [];
        sinistreId = lignes.find(l => l.sinistreTrackingId)?.sinistreTrackingId || null;
      } catch { /* ignore */ }

      await axiosInstance.post('/v1/courriers', {
        typeCourrier: 'SORTANT',
        nature: 'RELANCE',
        objet: vals.objet,
        expediteur: 'BNCB-TG',
        destinataire: b.destinataireOrganismeLibelle || b.destinataireLibre || '-',
        destinataireOrganismeTrackingId: b.destinataireOrganismeTrackingId || null,
        sinistreTrackingId: sinistreId,
        canal: vals.canal,
        dateCourrier: dayjs().format('YYYY-MM-DD'),
        referenceBordereau: b.numeroBordereau,
      });
      toast.success(t('bordereaux.relance.enregistree'));
      setRelanceModal(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || t('bordereaux.relance.erreur'));
    } finally {
      setSavingRelance(false);
    }
  };

  //  Décharge reçue 
  const confirmerDecharge = async (vals) => {
    try {
      await axiosInstance.patch(`/v1/bordereaux/${dechargeModal.bordereauTrackingId}/decharge-recue`, {
        observations: vals.observations || null,
      });
      toast.success(t('bordereaux.decharge.enregistree'));
      setDechargeModal(null);
      dechargeForm.resetFields();
      load();
    } catch (_) { /* message du serveur affiche par l'intercepteur */ }
  };

  // 
  // Colonnes liste principale
  // 
  const cols = [
    {
      title: t('bordereaux.colonnes.numero'), dataIndex: 'numeroBordereau', key: 'num', width: 240,
      render: v => <span style={{ fontFamily: 'var(--font-main)', fontWeight: 700, color: GREEN }}>{v}</span>,
    },
    {
      // `destinataireOrganismeLibelle` : c'est le nom que renvoie le serveur. L'ancien
      // `destinataireOrganismeNom` n'existait pas, et tout bureau homologue s'affichait « - ».
      /* UNE LARGEUR, ET PAS D'ELLIPSE. Cette colonne etait la seule sans `width` : en
         `table-layout: fixed` - ce que rc-table impose des qu'il y a `ellipsis` et un
         defilement horizontal - une colonne sans largeur prend ce qui RESTE entre
         `scroll.x` et la somme des largeurs declarees. Les autres en declaraient 1280
         pour un `scroll.x` de 1400 : il restait 120 px a une raison sociale, et
         l'ellipse faisait le reste.

         Le nom du destinataire est ce qu'on vient lire sur cette ligne. Il s'affiche
         donc en entier, sur deux lignes s'il le faut, et les mots sans espace sont
         coupes plutot que de deborder. */
      title: t('bordereaux.colonnes.destinataire'), key: 'dest', width: 260,
      export: (_, r) => r.destinataireOrganismeLibelle || r.destinataireLibre || '',
      render: (r) => (
        <span style={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.35 }}>
          {r.destinataireOrganismeLibelle || r.destinataireLibre || '-'}
        </span>
      ),
    },
    {
      title: t('bordereaux.colonnes.statut'), dataIndex: 'statut', key: 'statut', width: 100,
      export: v => STATUT_LABEL[v] || v || '',
      render: v => <Tag color={STATUT_COLOR[v] || 'default'} style={{ borderRadius: 20 }}>{STATUT_LABEL[v] || v}</Tag>,
    },
    {
      title: t('bordereaux.colonnes.transporteur'), dataIndex: 'transporteur', key: 'tr', width: 130,
      render: v => v ? <Tag style={{ borderRadius: 20, fontSize: 11 }}>{v}</Tag> : '-',
    },
    {
      title: t('bordereaux.colonnes.courriers'), dataIndex: 'nombreCourriers', key: 'nb', width: 90, align: 'center', numeric: true,
      export: v => v || 0,
      render: v => <Badge count={v || 0} showZero style={{ backgroundColor: GREEN }} />,
    },
    {
      title: t('bordereaux.colonnes.dateCreation'), dataIndex: 'dateCreation', key: 'dc', width: 130, format: 'date',
      render: v => v ? dayjs(v).format('DD/MM/YYYY') : '-',
    },
    {
      /* LE TEST ETAIT INVERSE : `statut !== 'REMIS_TRANSPORTEUR'` rendait « Remis au
         transporteur », donc quatre statuts sur cinq portaient une mention fausse - un
         brouillon jamais sorti du bureau s'annoncait comme remis. La regle vit
         desormais dans `ancienneteBordereau`, ou chaque statut est couvert par un
         test. */
      title: t('bordereaux.colonnes.anciennete'), key: 'anc', width: 150, exportable: false,
      render: (_, r) => {
        const { texte, enRetard } = ancienneteBordereau(r);
        return (
          <Tag color={enRetard ? 'red' : 'default'} icon={enRetard ? <ExclamationCircleOutlined /> : null}>
            {texte}
          </Tag>
        );
      },
    },
    {
      title: t('bordereaux.colonnes.relances'), dataIndex: 'nbRelances', key: 'rel', width: 100, align: 'center',
      export: v => v || 0,
      render: (v, r) => {
        if (!v) return <span style={{ color: '#bbb' }}>{t('bordereaux.aucuneRelance')}</span>;
        const tip = r.dateDerniereRelance
          ? t('bordereaux.derniereRelance', { date: dayjs(r.dateDerniereRelance).format('DD/MM/YYYY') })
          : null;
        const tag = <Tag color="orange" icon={<BellOutlined />}>{v}</Tag>;
        return tip ? <Tooltip title={tip}>{tag}</Tooltip> : tag;
      },
    },
    {
      /* LES ACTIONS SUIVENT LE STATUT, et la regle vit dans `actionsBordereau` ou
         chaque statut est couvert par un test. Les trois premieres - modifier, gerer
         les courriers, supprimer - n'existaient pas a l'ecran alors que le serveur les
         offre depuis toujours : c'est pourquoi un brouillon ne se modifiait pas. */
      title: t('bordereaux.colonnes.actions'), key: 'act', width: 420, exportable: false,
      render: (_, r) => (
        <Space size={4} wrap>
          {actionsPossibles(r).modifier && (
            <Tooltip title={t('bordereaux.modifierAide')}>
              <Button size="small" icon={<EditOutlined />}
                onClick={() => ouvrirModification(r)} />
            </Tooltip>
          )}
          {actionsPossibles(r).gererCourriers && (
            <Tooltip title={t('bordereaux.courriersAide')}>
              <Button size="small" icon={<MailOutlined />}
                onClick={() => ouvrirCourriers(r)}>
                {t('bordereaux.courriers')}
              </Button>
            </Tooltip>
          )}
          {actionsPossibles(r).supprimer && (
            <Tooltip title={t('bordereaux.suppression.aide')}>
              <Button size="small" danger icon={<DeleteOutlined />}
                onClick={() => supprimer(r)} />
            </Tooltip>
          )}
          <Tooltip title={t('bordereaux.imprimer')}>
            <Button size="small" icon={<PrinterOutlined />}
              style={{ borderColor: GREEN, color: GREEN }}
              onClick={() => imprimer(r)} />
          </Tooltip>
          {actionsPossibles(r).remis && (
            <Button size="small" icon={<SendOutlined />} type="primary" ghost
              style={{ borderColor: '#ffffff', color: '#ffffff' }}
              onClick={() => { setTransporteurModal(r); transporteurForm.resetFields(); }}>
              {t('bordereaux.remis')}
            </Button>
          )}
          {actionsPossibles(r).decharge && (
            <>
              {/* LA MEME REGLE QUE LA COLONNE « Ancienneté ». Le retard etait recalcule
                  ici, donc le bouton pouvait apparaitre sur un bordereau que la colonne
                  ne signalait pas en retard - ou l'inverse. Une seule definition, dans
                  `actionsBordereau`, qui s'appuie elle-meme sur `ancienneteBordereau`. */}
              {actionsPossibles(r).relancer && (
                <Tooltip title={t('bordereaux.relancerAide')}>
                  <Button size="small" icon={<BellOutlined />} type="primary" danger
                    onClick={() => ouvrirRelance(r)}>
                    {t('bordereaux.relancer')}
                  </Button>
                </Tooltip>
              )}
              <Button size="small" icon={<CheckCircleOutlined />} type="primary" ghost
                style={{ borderColor: '#ffffff', color: '#ffffff' }}
                onClick={() => { setDechargeModal(r); dechargeForm.resetFields(); }}>
                {t('bordereaux.decharge.bouton')}
              </Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  // 
  // Colonnes de saisie - celles de l'imprimé, plus le scan
  // 
  const colsSaisie = [
    {
      title: '#', key: 'n', width: 44, align: 'center',
      render: (_, __, idx) => <span style={{ color: '#888', fontWeight: 700 }}>{idx + 1}</span>,
    },
    {
      /* 320 et non 240 : le champ porte une recherche dont le seul texte
         indicatif - « N° sinistre, assuré… » - remplissait déjà la colonne, et
         les résultats y ajoutent le numéro ET le nom de l'assuré. Tronqué, on ne
         distinguait plus deux dossiers du même assuré. */
      title: t('bordereaux.saisie.colonnes.sinistre'), key: 'sin', width: 320,
      render: (_, l) => (
        <SinistreSearch
          key={l._id}
          value={l.sinistreTrackingId}
          onSelect={(tid, s) => onSinistreSelect(tid, s, l._id)}
          onChange={(v) => { if (!v) updateLigne(l._id, { sinistreTrackingId: null, numeroSinistreLocal: '' }); }}
          placeholder={t('bordereaux.saisie.sinistrePlaceholder')}
        />
      ),
    },
    {
      /* Une liste plutôt qu'un champ libre : les objets se répètent d'un
         bordereau à l'autre, et chacun les réécrivait à sa façon - « Transmission
         PV », « transmission du PV », « TRANSMISSION PV ». Un état filtré par
         objet n'y retrouvait rien. La liste s'allonge depuis le champ lui-même,
         et la saisie libre reste possible pour l'objet exceptionnel. */
      title: t('bordereaux.saisie.colonnes.objet'), key: 'obj', width: 280,
      render: (_, l) => (
        <SelectListeParametrable
          categorie="OBJET_COURRIER"
          value={l.objet}
          onChange={(v) => updateLigne(l._id, { objet: v })}
          placeholder={t('bordereaux.saisie.objetPlaceholder')}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: t('bordereaux.saisie.colonnes.nature'), key: 'nat', width: 190,
      render: (_, l) => (
        <Select value={l.nature} onChange={v => updateLigne(l._id, { nature: v })}
          placeholder={t('bordereaux.saisie.colonnes.nature')} style={{ width: '100%' }} allowClear showSearch optionFilterProp="label"
          options={NATURES.map(n => ({ value: n.value, label: t(`natures.${n.value}`) }))} />
      ),
    },
    {
      title: t('bordereaux.saisie.colonnes.reference'), key: 'ref', width: 160,
      render: (_, l) => (
        <Input value={l.referenceCourrier} onChange={e => updateLigne(l._id, { referenceCourrier: e.target.value })}
          placeholder={t('bordereaux.saisie.referencePlaceholder')} style={{ fontFamily: 'var(--font-main)', fontSize: 12 }} />
      ),
    },
    {
      // Le courrier signé est scanné AVANT de partir : c'est le seul moment où
      // il est encore entre nos mains. Sans dossier choisi, pas de GED cible.
      title: t('bordereaux.saisie.colonnes.scan'), key: 'piece', width: 220,
      render: (_, l) => (l.piece ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: '100%' }}>
          <PaperClipOutlined style={{ color: GREEN, flexShrink: 0 }} />
          <span title={l.piece.name}
            style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12, maxWidth: 140 }}>
            {l.piece.name}
          </span>
          <Button type="text" size="small" icon={<CloseOutlined />} aria-label={t('bordereaux.saisie.retirerScan')}
            onClick={() => updateLigne(l._id, { piece: null })} />
        </span>
      ) : (
        <Tooltip title={l.sinistreTrackingId
          ? t('bordereaux.saisie.scanAide')
          : t('bordereaux.saisie.scanSansDossierAide')}>
          <span>
            <Upload beforeUpload={(f) => { updateLigne(l._id, { piece: f }); return false; }}
              showUploadList={false} maxCount={1} accept=".pdf,.jpg,.jpeg,.png" disabled={!l.sinistreTrackingId}>
              <Button size="small" icon={<PaperClipOutlined />} disabled={!l.sinistreTrackingId}>{t('bordereaux.saisie.joindreScan')}</Button>
            </Upload>
          </span>
        </Tooltip>
      )),
    },
    {
      title: '', key: 'act', width: 84, align: 'center',
      render: (_, l, idx) => (
        <Space size={4}>
          <Tooltip title={t('bordereaux.saisie.dupliquer')}>
            <Button size="small" icon={<CopyOutlined />} onClick={() => dupLigne(idx)} />
          </Tooltip>
          {lignes.length > 1 && (
            <Tooltip title={t('bordereaux.saisie.supprimer')}>
              <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeLigne(l._id)} />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];
  const nbRenseignees = lignes.filter(ligneRenseignee).length;

  return (
    <Layout title={t('bordereaux.titre')}>
      {/*  En-tête  */}
      {/* `filtered` suit l'onglet actif et le filtre statut : c'est le seul nombre
          qui corresponde aux lignes du tableau. */}
      <PageHeader
        titre={t('bordereaux.titre')}
        icone={<FileDoneOutlined />}
        compte={filtered.length}
        unite={t('bordereaux.unite')}
        unitePluriel={t('bordereaux.unitePluriel')}
        actions={(
          <Button type="primary" icon={<PlusOutlined />}
            style={{ background: GREEN, borderColor: GREEN, borderRadius: 8 }}
            disabled={editionOuverte}
            onClick={ouvrirEdition}>
            {t('bordereaux.nouveau')}
          </Button>
        )}
      />

      {/* 
          Saisie d'un nouveau bordereau - un tableau en pleine page.
          Le bordereau physique EST un tableau : le remplir ligne à ligne
          dans un tiroir de 900 px obligeait à faire défiler chaque colonne.
          Ici les lignes se remplissent comme sur l'imprimé, et le scan du
          courrier signé part au dossier dans le même geste.
       */}
      {editionOuverte && (
        <div className="cb-card" style={{ marginBottom: 20, border: '1px solid #c7e3d1', background: '#fbfefc' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: GREEN }}>
                {bordereauEdite
                  ? t('bordereaux.saisie.titreModifier', { numero: bordereauEdite.numeroBordereau || '' })
                  : t('bordereaux.saisie.titre')}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>
                {bordereauEdite
                  /* ON LE DIT. `PUT /v1/bordereaux/{id}` ne touche pas aux lignes :
                     laisser croire le contraire ferait saisir des changements que le
                     serveur ignorerait en silence. */
                  ? t('bordereaux.saisie.aideModification')
                  : t('bordereaux.saisie.aide')}
              </div>
            </div>
            <Space>
              <Button onClick={annulerEdition}>{t('bordereaux.saisie.annuler')}</Button>
              <Button type="primary" loading={saving} icon={<FileAddOutlined />}
                style={{ background: GREEN, borderColor: GREEN }}
                onClick={enregistrer}>
                {bordereauEdite
                  ? t('bordereaux.saisie.enregistrerModifications')
                  : `${t('bordereaux.saisie.enregistrer')}${nbRenseignees > 0 ? ` (${nbRenseignees})` : ''}`}
              </Button>
            </Space>
          </div>

          <Form form={form} layout="vertical">
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item label={t('bordereaux.saisie.partenaire')} name="destinataireOrganismeTrackingId">
                  <Select allowClear placeholder={t('bordereaux.saisie.partenairePlaceholder')} showSearch optionFilterProp="label"
                    options={optionsPartenaires({
                      bureaux: bureaux.filter(b => b.typeOrganisme === 'BUREAU_HOMOLOGUE'),
                      compagnies: bureaux.filter(b => b.typeOrganisme === 'COMPAGNIE_MEMBRE'),
                    })} />
                </Form.Item>
              </Col>
              <Col xs={24} md={7}>
                <Form.Item label={t('bordereaux.saisie.destinataireLibre')} name="destinataireLibre">
                  <Input placeholder={t('bordereaux.saisie.destinataireLibrePlaceholder')} />
                </Form.Item>
              </Col>
              <Col xs={12} md={4}>
                <Form.Item label={t('bordereaux.saisie.lieuDepart')} name="lieuDepart" initialValue="Lomé">
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={12} md={5}>
                <Form.Item label={t('bordereaux.modeTransport')} name="transporteur" rules={[{ required: true, message: t('bordereaux.saisie.obligatoire') }]}>
                  <Select placeholder={t('bordereaux.saisie.choisir')}>
                    {TRANSPORTEURS.map(t => (
                      <Select.Option key={t.value} value={t.value}>{t.label}</Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Form>

          <Table
            className="cb-table"
            size="small"
            rowKey="_id"
            dataSource={lignes}
            columns={colsSaisie}
            pagination={false}
            /* CALCULE SUR SES PROPRES COLONNES - `colsSaisie`, pas `cols`. Le nombre
               etait ecrit a la main : « a 1080, Référence et Courrier scanné se
               retrouvaient comprimees au lieu de defiler ». Un nombre en dur se
               desynchronise de la somme des colonnes des la premiere ajoutee. */
            scroll={{ x: largeurTotale(colsSaisie) }}
          />

          <Button type="dashed" icon={<PlusOutlined />}
            style={{ marginTop: 12, width: '100%', borderColor: GREEN, color: GREEN }}
            onClick={addLigne}>
            {t('bordereaux.saisie.ajouterLigne')}
          </Button>
        </div>
      )}

      {/*  Onglets : Tous / À relancer  */}
      <Tabs
        activeKey={activeTab}
        onChange={(k) => { setActiveTab(k); setFiltreStatut(null); }}
        items={[
          { key: 'tous', label: <span>{t('bordereaux.onglets.tous')} <Badge count={list.length} showZero style={{ backgroundColor: '#999', marginLeft: 6 }} /></span> },
          {
            key: 'a-relancer',
            label: (
              <span>
                <BellOutlined /> {t('bordereaux.onglets.aRelancer')}
                <Badge count={aRelancerList.length} style={{ backgroundColor: '#cf1322', marginLeft: 6 }} />
              </span>
            ),
          },
        ]}
        style={{ marginBottom: 8 }}
      />

      {activeTab === 'a-relancer' && aRelancerList.length > 0 && (
        <Alert
          type="warning"
          showIcon
          icon={<BellOutlined />}
          style={{ marginBottom: 12, borderRadius: 6 }}
          message={t('bordereaux.alerteARelancer', { count: aRelancerList.length, jours: SEUIL_RELANCE_JOURS })}
          description={t('bordereaux.alerteARelancerAide')}
        />
      )}

      {/*  Filtres  */}
      <Row gutter={12} style={{ marginBottom: 16 }} className="cb-filter-bar">
        {activeTab === 'tous' && (
          <Col>
            <Select allowClear placeholder={t('bordereaux.filtrerStatut')} style={{ width: 200 }}
              value={filtreStatut} onChange={setFiltreStatut}>
              {Object.entries(STATUT_LABEL).map(([k, v]) => (
                <Select.Option key={k} value={k}>{v}</Select.Option>
              ))}
            </Select>
          </Col>
        )}
        <Col>
          <Button icon={<ReloadOutlined />} onClick={load}>{t('bordereaux.actualiser')}</Button>
        </Col>
      </Row>

      {/* BOUTON FLOTTANT, comme sur les autres listes de l'application - et comme
          sur l'onglet « Courriers » du meme ecran, qui l'avait deja. Il etait ici
          dans la barre de filtres : l'utilisateur qui passait d'un onglet a l'autre
          voyait le bouton changer de place.

          `floating` le pose en `position: fixed` : son emplacement dans le DOM n'a
          plus d'effet, et la colonne qui le portait n'a plus de raison d'etre. */}
      <TableExportButton
        floating
        data={filtered}
        columns={cols}
        count={filtered.length}
        filename="bordereaux-coursier"
        title={t('bordereaux.titre')}
        subtitle={
          activeTab === 'a-relancer' ? t('bordereaux.export.aRelancer', { jours: SEUIL_RELANCE_JOURS })
            : filtreStatut ? t('bordereaux.export.statut', { statut: STATUT_LABEL[filtreStatut] || filtreStatut }) : undefined
        }
      />

      {/*  Table liste  */}
      <Table
        columns={cols}
        dataSource={filtered}
        rowKey="bordereauTrackingId"
        loading={loading}
        size="middle"
        pagination={{ pageSize: 20, showTotal: n => t('bordereaux.total', { count: n }) }}
        /* CE TABLEAU N'AVAIT AUCUN `scroll.x`. Ses colonnes declarent une largeur, mais
           sans largeur totale le navigateur les comprime pour tenir dans la fenetre :
           sur un portable, « Destinataire » et « Ancienneté » se tassaient au lieu de
           defiler. La somme est calculee, donc elle suit les colonnes. */
        scroll={{ x: largeurTotale(cols) }}
      />

      {/*  Fenetre : courriers embarques d'un brouillon 
          POURQUOI UNE FENETRE SEPAREE DU FORMULAIRE. `PUT /v1/bordereaux/{id}` ne
          touche pas aux lignes : les courriers s'ajoutent et se retirent UN PAR UN, par
          deux points d'entree distincts. Les melanger au formulaire aurait laisse
          croire qu'un enregistrement les prend en compte.

          CHAQUE GESTE PART AUSSITOT AU SERVEUR, et la fenetre se recharge. Pas de panier
          a valider : il faudrait alors rejouer une suite d'ajouts et de retraits, et un
          echec au milieu laisserait le bordereau dans un etat que personne n'a voulu. */}
      <CBModal
        open={!!courriersModal}
        title={courriersModal
          ? t('bordereaux.embarques.titre', { numero: courriersModal.numeroBordereau || '' })
          : ''}
        onCancel={() => { setCourriersModal(null); setCandidats([]); }}
        width="min(1000px, 94vw)"
        destroyOnClose
        footer={(
          <Button onClick={() => { setCourriersModal(null); setCandidats([]); }}>{t('bordereaux.embarques.fermer')}</Button>
        )}
      >
        {courriersModal && (
          <>
            <div style={{ fontSize: 13, fontWeight: 700, color: GREEN, marginBottom: 8 }}>
              {t('bordereaux.embarques.embarques', { n: (courriersModal.lignes || []).length })}
            </div>
            <Table
              className="cb-table" size="small" pagination={false}
              rowKey={(l) => l.courrierTrackingId}
              dataSource={courriersModal.lignes || []}
              locale={{ emptyText: t('bordereaux.embarques.aucun') }}
              columns={[
                { title: t('bordereaux.embarques.colonnes.ordre'), dataIndex: 'ordreDansBordereau', key: 'ord', width: 60, align: 'center' },
                { title: t('bordereaux.embarques.colonnes.sinistre'), dataIndex: 'numeroSinistreOurRef', key: 'sin', width: 180 },
                {
                  title: t('bordereaux.embarques.colonnes.objet'), dataIndex: 'objet', key: 'obj',
                  render: (v, l) => l.objetDetaille || (NATURES.some((n) => n.value === v) ? t(`natures.${v}`) : v) || '-',
                },
                { title: t('bordereaux.embarques.colonnes.reference'), dataIndex: 'referenceCourrier', key: 'ref', width: 160 },
                {
                  title: '', key: 'act', width: 100, align: 'center',
                  render: (_, l) => (
                    <Button size="small" danger icon={<CloseOutlined />} loading={occupe}
                      onClick={() => retirerCourrier(l.courrierTrackingId)}>
                      {t('bordereaux.embarques.retirer')}
                    </Button>
                  ),
                },
              ]}
            />

            <div style={{ fontSize: 13, fontWeight: 700, color: GREEN, margin: '20px 0 8px' }}>
              {t('bordereaux.embarques.prets', { n: candidats.length })}
            </div>
            {/* FILTRES SUR LE DESTINATAIRE du bordereau - voir `ouvrirCourriers`.
                Embarquer un courrier adresse a quelqu'un d'autre est l'erreur que ce
                filtre previent, et le serveur l'accepterait sans broncher. */}
            <Table
              className="cb-table" size="small"
              pagination={{ pageSize: 10, showTotal: (n) => t('bordereaux.embarques.total', { count: n }) }}
              rowKey="courrierTrackingId"
              dataSource={candidats}
              locale={{ emptyText: t('bordereaux.embarques.aucunPret') }}
              columns={[
                { title: t('bordereaux.embarques.colonnes.sinistre'), dataIndex: 'sinistreNumeroLocal', key: 'sin', width: 180 },
                { title: t('bordereaux.embarques.colonnes.objet'), dataIndex: 'objet', key: 'obj', ellipsis: true },
                { title: t('bordereaux.embarques.colonnes.reference'), dataIndex: 'referenceCourrier', key: 'ref', width: 160 },
                {
                  title: '', key: 'act', width: 110, align: 'center',
                  render: (_, c) => (
                    <Button size="small" type="primary" ghost icon={<PlusOutlined />} loading={occupe}
                      style={{ borderColor: GREEN, color: '#ffffff' }}
                      onClick={() => ajouterCourrier(c.courrierTrackingId)}>
                      {t('bordereaux.embarques.ajouter')}
                    </Button>
                  ),
                },
              ]}
            />
          </>
        )}
      </CBModal>

      {/* 
          Modal : Remis transporteur
       */}
      <CBModal
        open={!!transporteurModal}
        title={t('bordereaux.remise.titre')}
        onCancel={() => setTransporteurModal(null)}
        onOk={() => transporteurForm.submit()}
        okText={t('bordereaux.remise.ok')}
        okButtonProps={{ style: { background: GREEN, borderColor: GREEN } }}
      >
        <Form form={transporteurForm} layout="vertical" onFinish={confirmerRemis}>
          <Form.Item label={t('bordereaux.modeTransport')} name="transporteur" rules={[{ required: true }]}>
            <Select>
              {TRANSPORTEURS.map(t => (
                <Select.Option key={t.value} value={t.value}>{t.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label={t('bordereaux.remise.compagnieBus')} name="nomCompagnieBus">
            <Input placeholder={t('bordereaux.remise.compagnieBusPlaceholder')} />
          </Form.Item>
          <Form.Item label={t('bordereaux.remise.reference')} name="referenceTransporteur">
            <Input />
          </Form.Item>
          <Form.Item label={t('bordereaux.remise.montant')} name="montantTransporteur">
            <CBInputNumber style={{ width: '100%' }} min={0} step={500} />
          </Form.Item>
        </Form>
      </CBModal>

      {/* 
          Modal : Décharge reçue
       */}
      <CBModal
        open={!!dechargeModal}
        title={t('bordereaux.decharge.titre')}
        onCancel={() => setDechargeModal(null)}
        onOk={() => dechargeForm.submit()}
        okText={t('bordereaux.decharge.ok')}
        okButtonProps={{ style: { background: GREEN, borderColor: GREEN } }}
      >
        <Form form={dechargeForm} layout="vertical" onFinish={confirmerDecharge}>
          <Form.Item label={t('bordereaux.decharge.observations')} name="observations">
            <Input.TextArea rows={3} placeholder={t('bordereaux.decharge.observationsPlaceholder')} />
          </Form.Item>
        </Form>
      </CBModal>

      {/* 
          Modal : Relance bordereau sans décharge
       */}
      <CBModal
        open={!!relanceModal}
        title={t('bordereaux.relance.titre', { numero: relanceModal?.numeroBordereau || '' })}
        onCancel={() => setRelanceModal(null)}
        onOk={() => relanceForm.submit()}
        confirmLoading={savingRelance}
        okText={t('bordereaux.relance.ok')}
        okButtonProps={{ danger: true, type: 'primary' }}
      >
        <Form form={relanceForm} layout="vertical" onFinish={envoyerRelance}>
          <Form.Item label={t('bordereaux.relance.canal')} name="canal" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'MAIL', label: t('bordereaux.relance.canaux.MAIL') },
                { value: 'COURRIER_POSTAL', label: t('bordereaux.relance.canaux.COURRIER_POSTAL') },
                { value: 'PHYSIQUE', label: t('bordereaux.relance.canaux.PHYSIQUE') },
                { value: 'FAX', label: t('bordereaux.relance.canaux.FAX') },
              ]}
            />
          </Form.Item>
          <Form.Item label={t('bordereaux.relance.objet')} name="objet" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label={t('bordereaux.relance.note')} name="observations">
            <Input.TextArea rows={5} />
          </Form.Item>
          <div style={{ fontSize: 11.5, color: '#888' }}>
            <Trans t={t} i18nKey="bordereaux.relance.aide" components={{ strong: <strong /> }} />
          </div>
        </Form>
      </CBModal>
    </Layout>
  );
}

